import { makeOrb, removeObj, tint, clamp } from "../shared";

class Wave {
  constructor(ctx) { this.ctx = ctx; this.writesColors = true; this.sources = []; this.markers = []; this.phase = 0; this.alloc(); this.applyPattern(); }
  alloc() { const n = this.ctx.count; this.u = new Float32Array(n); this.up = new Float32Array(n); this.mask = new Uint8Array(n); }
  resize() { this.alloc(); this.applyPattern(true); }
  applyPattern(keepSources) {
    const P = this.ctx.params, N = this.ctx.N; this.mask.fill(0); this.plane = false;
    if (!keepSources) this.sources = P.pattern === "interference" ? [{ x: -1.3, z: -3 }, { x: 1.3, z: -3 }] : P.pattern === "ripple" ? [{ x: 0, z: 0 }] : [];
    if (P.pattern === "slit") {
      this.plane = true;
      const wc = Math.floor(N * 0.32), gap = Math.max(2, Math.round(N * 0.035)), s1 = Math.round(N * 0.42), s2 = Math.round(N * 0.58);
      for (let r = 0; r < N; r++) if (Math.abs(r - s1) > gap && Math.abs(r - s2) > gap) { this.mask[r * N + wc] = 1; this.mask[r * N + wc + 1] = 1; }
    }
    this.u.fill(0); this.up.fill(0); this.syncMarkers();
  }
  syncMarkers() {
    this.markers.forEach((m) => removeObj(this.ctx.overlay, m));
    this.markers = this.sources.map((s) => { const m = makeOrb(this.ctx.overlay, 0xffb800, 0.14); m.position.set(s.x, this.ctx.top + 0.35, s.z); return m; });
  }
  cell(x, z) { const { N, sp, half } = this.ctx; return [clamp(Math.floor((z + half) / sp), 1, N - 2), clamp(Math.floor((x + half) / sp), 1, N - 2)]; }
  drop(x, z, amt) {
    const N = this.ctx.N, [r0, c0] = this.cell(x, z), R = 3;
    for (let r = -R; r <= R; r++) for (let c = -R; c <= R; c++) {
      const rr = r0 + r, cc = c0 + c; if (rr < 1 || cc < 1 || rr > N - 2 || cc > N - 2) continue;
      this.u[rr * N + cc] -= amt * Math.exp(-(r * r + c * c) / 3);
    }
  }
  pointer(e) {
    const P = this.ctx.params;
    if (e.type === "down") {
      if (P.tool === "source" && P.pattern !== "standing") { if (this.sources.length < 8) { this.sources.push({ x: e.x, z: e.z }); this.syncMarkers(); } }
      else this.drop(e.x, e.z, 1.6 * P.amp * this.ctx.g.force * e.pressure);
      this.last = e;
    } else if (e.type === "move" && this.last && P.tool === "drop") {
      if (Math.hypot(e.x - this.last.x, e.z - this.last.z) > 0.25) { this.drop(e.x, e.z, 0.6 * P.amp * this.ctx.g.force); this.last = e; }
    } else if (e.type === "up") this.last = null;
  }
  step(dt, t) {
    const P = this.ctx.params, { N, target, colors } = this.ctx, f = this.ctx.g.force, A = P.amp * f;
    if (P.pattern === "standing") {
      const w = Math.cos(2 * Math.PI * P.freq * 0.25 * t);
      for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
        const i = r * N + c, h = A * Math.sin((P.modeN * Math.PI * (c + 0.5)) / N) * Math.sin((P.modeM * Math.PI * (r + 0.5)) / N) * w;
        target[i] = clamp(h * 0.9, -1, 1); tint(colors, i, h, 0.9); this.u[i] = h;
      }
      return;
    }
    const subs = clamp(Math.round(3 * this.ctx.g.simSpeed), 1, 10), C2 = P.c, damp = 1 - P.damping, u = this.u, mask = this.mask;
    for (let s = 0; s < subs; s++) {
      this.phase += (2 * Math.PI * P.freq) / 180;
      const drive = A * Math.sin(this.phase);
      for (const src of this.sources) {
        const [r0, c0] = this.cell(src.x, src.z);
        for (let r = -1; r <= 1; r++) for (let c = -1; c <= 1; c++) u[(r0 + r) * N + c0 + c] = drive * (r || c ? 0.7 : 1);
      }
      if (this.plane) for (let r = 1; r < N - 1; r++) u[r * N + 2] = drive * 0.8;
      const nx = this.up;
      for (let r = 1; r < N - 1; r++) for (let c = 1; c < N - 1; c++) {
        const i = r * N + c;
        if (mask[i]) { nx[i] = 0; continue; }
        nx[i] = (2 * u[i] - nx[i] + C2 * (u[i - 1] + u[i + 1] + u[i - N] + u[i + N] - 4 * u[i])) * damp;
      }
      if (P.absorb) {
        const W = Math.max(4, Math.round(N * 0.06));
        for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
          const d = Math.min(r, c, N - 1 - r, N - 1 - c);
          if (d < W) nx[r * N + c] *= 1 - 0.08 * ((W - d) / W);
        }
      }
      this.up = u; this.u = nx;
    }
    const uu = this.u;
    for (let i = 0; i < uu.length; i++) {
      if (mask[i]) { target[i] = 1; colors[i * 3] = 0.35; colors[i * 3 + 1] = 0.4; colors[i * 3 + 2] = 0.5; continue; }
      target[i] = clamp(uu[i] * 0.9, -1, 1); tint(colors, i, uu[i], 0.9);
    }
  }
  onParams(key) { if (key === "pattern") this.applyPattern(); }
  action(id) {
    if (id === "pulse") this.drop(0, 0, 2.5 * this.ctx.params.amp * this.ctx.g.force);
    if (id === "clearSources") { this.ctx.pushHistory(); this.sources = []; this.syncMarkers(); }
  }
  snapshot() { return this.sources.map((s) => ({ ...s })); }
  restore(s) { this.sources = s.map((x) => ({ ...x })); this.syncMarkers(); }
  clear() { this.sources = []; this.u.fill(0); this.up.fill(0); this.syncMarkers(); }
  telemetry() {
    const P = this.ctx.params; let e = 0; for (const v of this.u) e += v * v;
    const lam = ((Math.sqrt(P.c) * 180) / P.freq) * this.ctx.sp;
    return [["Pattern", P.pattern.toUpperCase()], ["Sources", this.sources.length + (this.plane ? " + plane" : "")], ["Wavelength λ", P.pattern === "standing" ? `n=${P.modeN} m=${P.modeM}` : `${lam.toFixed(2)} u`], ["Wave energy", e.toFixed(1)]];
  }
  dispose() { this.markers.forEach((m) => removeObj(this.ctx.overlay, m)); }
}

const wave = {
  id: "wave-lab", name: "Wave Lab", icon: "Waves", rest: 0,
  desc: "A finite-difference solver of the 2D wave equation runs across the tank surface. Tap to drop ripples or place oscillators, watch interference fringes, diffraction through a double slit, and resonant standing-wave modes.",
  math: "∂²u/∂t² = c²∇²u − γ ∂u/∂t",
  params: [
    { key: "pattern", label: "Experiment", type: "select", options: [["ripple", "Free ripples"], ["interference", "Interference"], ["slit", "Double slit"], ["standing", "Standing wave"]], def: "interference" },
    { key: "tool", label: "Tap action", type: "select", options: [["drop", "Drop ripple"], ["source", "Place oscillator"]], def: "drop" },
    { key: "freq", label: "Frequency", type: "range", min: 0.5, max: 8, step: 0.1, def: 3 },
    { key: "amp", label: "Amplitude", type: "range", min: 0.2, max: 2, step: 0.05, def: 0.9 },
    { key: "c", label: "Wave speed (c²)", type: "range", min: 0.05, max: 0.45, step: 0.01, def: 0.3 },
    { key: "damping", label: "Damping γ", type: "range", min: 0, max: 0.02, step: 0.001, def: 0.002 },
    { key: "absorb", label: "Absorbing edges", type: "toggle", def: true },
    { key: "modeN", label: "Standing mode n", type: "range", min: 1, max: 8, step: 1, def: 3 },
    { key: "modeM", label: "Standing mode m", type: "range", min: 1, max: 8, step: 1, def: 2 },
  ],
  actions: [{ id: "pulse", label: "Center pulse" }, { id: "clearSources", label: "Remove oscillators" }],
  create: (ctx) => new Wave(ctx),
};
export default wave;
